/**
 * Lecture des communications de paiement.
 *
 * Trois formats coexistent dans les relevés bancaires réels :
 *
 * 1. La communication structurée belge — 12 chiffres dont 2 de contrôle en
 *    modulo 97, transportée par la banque dans un champ dédié. C'est le seul
 *    format qu'une application bancaire valide à la saisie : une faute de frappe
 *    du parent est refusée avant que l'argent ne parte.
 *
 * 2. Le format historique `CDV-<TAG>-<AASEQ>`, saisi à la main par le parent
 *    dans un champ de texte libre. Aucun contrôle, donc les variantes existent
 *    (`CDV- WBK-2601962`, espaces, minuscules).
 *
 * 3. Les annotations de remboursement, écrites par l'administration :
 *    `CDV-MC-2601563 Remb CM - NC-26-00417`. Un remboursement peut porter
 *    plusieurs notes de crédit.
 *
 * Le champ de communication du CODA fait 53 caractères et **tronque** au-delà.
 * On le détecte pour signaler les cas irrécupérables plutôt que de les rater en
 * silence.
 */

/** Longueur du champ communication dans un enregistrement CODA de type 21. */
export const CODA_COMMUNICATION_LENGTH = 53;

/**
 * Types de communication structurée observés en production.
 * `101` et `102` portent une référence de paiement valide.
 * `106` porte la ventilation TVA des relevés de frais bancaires : ce n'est
 * PAS une référence de paiement, et un parseur qui prend « 12 chiffres après le
 * drapeau structuré » l'avale par erreur.
 */
export const PAYMENT_STRUCTURED_TYPES = ['101', '102'] as const;

export type CommunicationKind = 'structured' | 'legacy' | 'unstructured';

export interface ParsedCommunication {
  kind: CommunicationKind;
  /** Le texte tel que la banque l'a transmis, sans transformation. */
  raw: string;
  /** Les 12 chiffres, uniquement pour une communication structurée valide. */
  structuredDigits?: string;
  /** Rendu lisible `+++123/4567/89012+++`. */
  structuredFormatted?: string;
  /** Référence de facture au format historique, normalisée. */
  legacyInvoiceRef?: string;
  /** Notes de crédit citées, normalisées. Un remboursement peut en porter plusieurs. */
  creditNoteRefs: string[];
  /**
   * Le CONTENU de la communication occupe toute la largeur du champ de 53
   * caractères : elle est probablement coupée, et une référence peut avoir été
   * perdue. À faire vérifier à la main.
   *
   * Se mesure sur le contenu, pas sur le champ : le champ CODA est toujours
   * complété à droite par des espaces, donc sa longueur ne dit rien.
   */
  possiblyTruncated: boolean;
  /**
   * La référence a été lue avec une tolérance sur le préfixe : le parent a
   * écrit autre chose que `CDV`. Vu une fois sur les relevés réels —
   * `CDW-WBK-2601723`, 35,00 €, une seule lettre de travers.
   *
   * À signaler dans l'écran de contrôle : un humain doit pouvoir y jeter un œil.
   */
  toleratedPrefix?: boolean;
  /**
   * La référence ne vient pas du champ communication mais du texte libre.
   *
   * Certains virements instantanés arrivent avec un champ communication rempli
   * de débris XML ISO 20022 — `.CdtrRefInf..Tp..CdOrPrtry..Cd.SCOR.` — alors que
   * le texte libre porte la vraie référence après « Communication : ». Sept cas
   * sur les relevés de juin-juillet, tous des communications structurées
   * valides.
   */
  recoveredFromFreeText?: boolean;
}

/**
 * Chiffres de contrôle d'une communication structurée belge.
 * Le reste vaut 97 quand la division tombe juste — écrire `00` est l'erreur
 * classique, et elle produit une communication que les banques refusent.
 */
export function mod97CheckDigits(base: string): string {
  if (!/^\d{10}$/.test(base)) {
    throw new Error(`base attendue sur 10 chiffres, reçu ${JSON.stringify(base)}`);
  }
  // On réduit chiffre par chiffre pour rester exact au-delà de Number.MAX_SAFE_INTEGER.
  let remainder = 0;
  for (const digit of base) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return String(remainder === 0 ? 97 : remainder).padStart(2, '0');
}

/** Vrai si les 12 chiffres forment une communication structurée valide. */
export function isValidStructured(digits: string): boolean {
  if (!/^\d{12}$/.test(digits)) return false;
  return mod97CheckDigits(digits.slice(0, 10)) === digits.slice(10);
}

/** Construit une communication structurée à partir d'une base de 10 chiffres. */
export function buildStructured(base: string): string {
  return base + mod97CheckDigits(base);
}

/** Rend une communication structurée lisible : `+++123/4567/89012+++`. */
export function formatStructured(digits: string): string {
  if (!/^\d{12}$/.test(digits)) {
    throw new Error(`12 chiffres attendus, reçu ${JSON.stringify(digits)}`);
  }
  return `+++${digits.slice(0, 3)}/${digits.slice(3, 7)}/${digits.slice(7)}+++`;
}

/**
 * Normalisation utilisée pour comparer deux références : majuscules, et tout ce
 * qui n'est ni lettre ni chiffre est retiré.
 *
 * C'est ce qui rattrape la classe d'erreurs la plus fréquente sur le format
 * historique — `CDV- WBK-2601962`, ` CDV-MC-2601654`, `cdv wbk 2601586`
 * deviennent tous la même chaîne.
 */
export function normalizeReference(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Référence de facture historique, normalisée : `CDV` + tag + 7 chiffres. */
const LEGACY_INVOICE = /CDV([A-Z]+)(\d{7})/;

/**
 * Le même format, avec une lettre fautive à la place du `V`.
 *
 * S'applique au texte BRUT, pas au texte normalisé, et exige les tirets. C'est
 * ce qui rend le motif sûr : `CDW-WBK-2601723` passe, une chaîne quelconque
 * contenant `CD` suivi de lettres et de sept chiffres ne passe pas. Et de toute
 * façon les sept chiffres doivent correspondre à une facture existante.
 */
const LEGACY_TYPO = /\bCD[A-Z]\s*-\s*([A-Z]{2,5})\s*-\s*(\d{7})\b/i;

/**
 * Le tag du centre suivi des sept chiffres, sans le préfixe `CDV` : `NDSC-2601466`.
 *
 * Le tiret est exigé, comme pour la faute de frappe : c'est lui qui distingue
 * une référence d'une suite de caractères quelconque. La sûreté finale ne vient
 * toutefois pas d'ici mais du rapprochement, qui exige que le tag cité soit
 * celui de la facture trouvée.
 */
export const LEGACY_BARE_TAG = /\b([A-Z]{2,5})\s*-\s*(\d{7})\b/i;

/**
 * Tags qui ne désignent jamais un centre, et qu'on ne veut donc pas lire comme
 * une référence de facture même sous forme tolérée.
 *
 * `NC` est le préfixe des notes de crédit, `FACT` celui de la facturation
 * partenaires. Tous deux apparaissent réellement dans les communications.
 *
 * ## `CDV` en fait partie, et son absence coûtait une référence fabriquée
 *
 * `CDV` est le PRÉFIXE, jamais un tag de centre. Sans lui dans cette liste,
 * une communication mal frappée d'un chiffre — `CDV-2506002-00072`, où
 * `250602` a reçu un zéro de trop — échouait aux deux motifs stricts, tombait
 * dans la tolérance du tag nu, et `CDV` s'y faisait lire comme un centre :
 * la référence reconstruite était `CDV-CDV-2506002`.
 *
 * Elle n'a jamais désigné aucune facture, et elle ne le pouvait pas. Mais elle
 * était ÉCRITE dans `legacy_invoice_ref`, et l'écran préremplissait la
 * recherche à la main avec — « CDV-CDV-2506002 » proposé en toutes lettres à
 * quelqu'un qui cherchait `CDV-250602-00072`. Mesuré le 20 septembre 2026 :
 * 1 remboursement et 4 paiements portent cette forme.
 *
 * Sans `CDV` ici, une référence sans tag garde encore ses sept chiffres via
 * `allLegacyRefs`, qui les cherche dans le numéro : rien n'est perdu, et
 * l'invention disparaît.
 */
export const TAGS_EXCLUS = new Set(['CDV', 'NC', 'FACT', 'IBAN', 'BIC', 'TVA', 'VAT', 'SCOR', 'REF']);
/** Note de crédit citée dans une communication de remboursement. */
const CREDIT_NOTE_GLOBAL = /NC(\d{2})(\d{5})/g;

/**
 * Extrait les notes de crédit citées. Les tirets ayant déjà été retirés par la
 * normalisation, `NC-26-00417` se présente ici comme `NC2600417`.
 */
function extractCreditNotes(normalized: string): string[] {
  const refs = new Set<string>();
  for (const match of normalized.matchAll(CREDIT_NOTE_GLOBAL)) {
    refs.add(`NC-${match[1]}-${match[2]}`);
  }
  return [...refs];
}

/**
 * Lit une communication.
 *
 * @param raw            le texte transmis par la banque
 * @param structuredType le type de communication structurée quand la banque en
 *                       signale une (`101`, `102`, `106`…). Absent pour une
 *                       communication libre.
 */
export function parseCommunication(
  raw: string,
  structuredType?: string,
): ParsedCommunication {
  const trimmed = raw.trim();
  const normalized = normalizeReference(trimmed);
  // Le champ CODA fait TOUJOURS 53 caracteres, complete a droite par des
  // espaces. Mesurer `raw.length` reviendrait donc a declarer toute
  // communication libre comme tronquee — sur 22 fichiers reels, cela signalait
  // 208 cas au lieu de 8. Ce qui compte est la longueur du CONTENU : s'il
  // occupe toute la largeur, quelque chose a pu etre coupe.
  const possiblyTruncated = raw.trimEnd().length >= CODA_COMMUNICATION_LENGTH;
  const creditNoteRefs = extractCreditNotes(normalized);

  // 1. Communication structurée annoncée par la banque, dans un champ dédié.
  if (structuredType && (PAYMENT_STRUCTURED_TYPES as readonly string[]).includes(structuredType)) {
    const digits = normalized.replace(/\D/g, '').slice(0, 12);
    if (isValidStructured(digits)) {
      return {
        kind: 'structured',
        raw,
        structuredDigits: digits,
        structuredFormatted: formatStructured(digits),
        creditNoteRefs,
        possiblyTruncated: false,
      };
    }
  }

  // 2. Format historique, y compris les annotations de remboursement.
  const legacy = normalized.match(LEGACY_INVOICE);
  if (legacy) {
    return {
      kind: 'legacy',
      raw,
      legacyInvoiceRef: `CDV-${legacy[1]}-${legacy[2]}`,
      creditNoteRefs,
      possiblyTruncated,
    };
  }

  // 2 bis. Le même format avec une faute sur le préfixe. Tenté seulement après
  //        l'échec du motif strict, pour ne jamais dégrader un cas propre.
  const typo = trimmed.match(LEGACY_TYPO);
  if (typo) {
    return {
      kind: 'legacy',
      raw,
      legacyInvoiceRef: `CDV-${typo[1].toUpperCase()}-${typo[2]}`,
      creditNoteRefs,
      possiblyTruncated,
      toleratedPrefix: true,
    };
  }

  // 2 ter. Le tag du centre et les sept chiffres, sans le préfixe `CDV`.
  //
  //        Cas réel : `NDSC-2601466`, pour la facture `CDV-NDSC-2601466`. Le
  //        parent recopie la partie qui lui parle et laisse tomber le `CDV-`.
  //        Sans cette tolérance le paiement partait en file manuelle sans
  //        aucune piste, alors que la référence est complète et sans ambiguïté.
  //
  //        Ce qui rend le motif sûr n'est pas le motif : c'est la
  //        **vérification du tag** faite au rapprochement. `allLegacyRefs`
  //        marque ces références comme tolérées, et `planOne` n'accepte la
  //        facture trouvée par les sept chiffres que si son propre tag est
  //        celui qui a été cité. Un `REF-1234567` quelconque ne peut donc pas
  //        se faire passer pour une facture.
  const nu = trimmed.match(LEGACY_BARE_TAG);
  if (nu && !TAGS_EXCLUS.has(nu[1].toUpperCase())) {
    return {
      kind: 'legacy',
      raw,
      legacyInvoiceRef: `CDV-${nu[1].toUpperCase()}-${nu[2]}`,
      creditNoteRefs,
      possiblyTruncated,
      toleratedPrefix: true,
    };
  }

  // 3. Une communication structurée tapée en texte libre par le parent, parfois
  //    accompagnée du nom de l'enfant : `506/6106/18276 kabuaya pinda lael P5B1`.
  //    On ne la retient que si le modulo 97 est bon — sinon ce sont des chiffres
  //    quelconques (un numéro de client, une date) et l'accepter fabriquerait un
  //    faux rapprochement.
  const digitRuns = trimmed.match(/\d[\d\s/.-]{10,}\d/g) ?? [];
  for (const run of digitRuns) {
    const digits = run.replace(/\D/g, '');
    for (let i = 0; i + 12 <= digits.length; i++) {
      const candidate = digits.slice(i, i + 12);
      if (isValidStructured(candidate)) {
        return {
          kind: 'structured',
          raw,
          structuredDigits: candidate,
          structuredFormatted: formatStructured(candidate),
          creditNoteRefs,
          possiblyTruncated,
        };
      }
    }
  }

  return { kind: 'unstructured', raw, creditNoteRefs, possiblyTruncated };
}

/**
 * Formes EXPLICITES d'une communication structurée dans du texte libre.
 *
 * `+++123/4567/89012+++`, `***123/4567/89012***`, ou les douze chiffres qui
 * suivent un marqueur de référence (`Ref.`, `SCOR`). Rien d'autre.
 */
const STRUCTURED_EXPLICITE = [
  /[+*]{3}\s*(\d{3})\s*\/\s*(\d{4})\s*\/\s*(\d{5})\s*[+*]{3}/,
  /\bref[\s.:\/]*([\d][\d\s]{10,18}\d)/i,
  /\bscor[\s.:\/]*([\d][\d\s]{10,18}\d)/i,
];

/**
 * Tente de retrouver une référence dans le texte libre du mouvement.
 *
 * Sert quand le champ communication de l'enregistrement 21 ne donne rien
 * d'exploitable. Deux situations rencontrées sur les relevés réels : le champ
 * rempli de débris XML ISO 20022 (`.CdtrRefInf..Tp..CdOrPrtry..Cd.SCOR.`) alors
 * que le texte libre porte la vraie référence après `Ref.`, et le champ tronqué
 * à 53 caractères dont le texte libre contient la fin.
 *
 * ## Pourquoi cette fonction ne délègue pas à `parseCommunication`
 *
 * Parce qu'un premier essai l'a fait, et qu'il **fabriquait des références**.
 *
 * `parseCommunication` cherche, en dernier recours, n'importe quelle suite de
 * douze chiffres dont le modulo 97 tombe juste. Sur les 53 caractères du champ
 * communication, c'est raisonnable. Sur 150 caractères de texte libre — qui
 * contiennent l'adresse du donneur d'ordre, son IBAN, un code postal et un
 * horodatage — la fenêtre glissante trouve un modulo 97 valide environ une fois
 * sur 97 essais, et il y a des dizaines de fenêtres.
 *
 * Mesuré : 51 des 54 paiements sans référence se voyaient attribuer une fausse
 * communication structurée. `063430153912` venait d'un IBAN dans une adresse,
 * `091000194433` d'un virement de commune. Une allocation aurait été proposée
 * sur une facture qui n'avait rien à voir.
 *
 * D'où la règle : ici, une communication structurée n'est acceptée que sous une
 * **forme explicite** — `+++…+++`, ou derrière un marqueur `Ref.` / `SCOR`.
 * Jamais par balayage.
 */
export function recoverFromFreeText(freeText: string): ParsedCommunication | null {
  if (!freeText) return null;

  // 1. Une référence historique. La forme `CDV-<TAG>-<7 chiffres>` est assez
  //    spécifique pour être cherchée dans tout le texte sans risque.
  const normalise = normalizeReference(freeText);
  const legacy = normalise.match(LEGACY_INVOICE);
  if (legacy) {
    return {
      kind: 'legacy',
      raw: freeText.trim(),
      legacyInvoiceRef: `CDV-${legacy[1]}-${legacy[2]}`,
      creditNoteRefs: extractCreditNotes(normalise),
      possiblyTruncated: false,
      recoveredFromFreeText: true,
    };
  }

  const typo = freeText.match(LEGACY_TYPO);
  if (typo) {
    return {
      kind: 'legacy',
      raw: freeText.trim(),
      legacyInvoiceRef: `CDV-${typo[1].toUpperCase()}-${typo[2]}`,
      creditNoteRefs: extractCreditNotes(normalise),
      possiblyTruncated: false,
      recoveredFromFreeText: true,
      toleratedPrefix: true,
    };
  }

  // 2. Une communication structurée, sous forme explicite uniquement.
  for (const motif of STRUCTURED_EXPLICITE) {
    const m = freeText.match(motif);
    if (!m) continue;
    const digits = m.slice(1).join('').replace(/\D/g, '').slice(0, 12);
    if (isValidStructured(digits)) {
      return {
        kind: 'structured',
        raw: m[0].trim(),
        structuredDigits: digits,
        structuredFormatted: formatStructured(digits),
        creditNoteRefs: [],
        possiblyTruncated: false,
        recoveredFromFreeText: true,
      };
    }
  }

  return null;
}
